export enum BookingErrorType {
    AlreadyBooked = 'AlreadyBooked',
    Coded = 'Coded',
    Unknown = 'Unknown'
}

export type WalkingBookingErrorCode =
    | 'session_full'
    | 'session_not_available'
    | 'address_out_of_zone'
    | 'staff_unavailable'
    | 'outside_lead_time'
    | 'duplicate_pet_service_day';

export interface BookingErrorDetail {
    code?: string;
    message?: string;
    field?: string;
    appointmentUuid?: string;
}

export class BookingError extends Error {
    name = 'BookingError';
    type: BookingErrorType;
    code?: WalkingBookingErrorCode | string;
    details: Array<BookingErrorDetail>;

    constructor(
        type: BookingErrorType,
        message?: string,
        code?: WalkingBookingErrorCode | string,
        details: Array<BookingErrorDetail> = []
    ) {
        super(message ?? BookingErrorMessages[type]);

        this.type = type;
        this.code = code;
        this.details = details;
    }
}

export const BookingErrorMessages: Record<BookingErrorType, string> = {
    [BookingErrorType.AlreadyBooked]:
        'You already booked an appointment for this pet today. Please cancel previous appointment to continue booking.',
    [BookingErrorType.Coded]: 'That time is no longer available. Please pick another time.',
    [BookingErrorType.Unknown]: 'Oops, something went wrong.'
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const toBookingErrorDetail = (entry: Record<string, unknown>): BookingErrorDetail => ({
    code: typeof entry.code === 'string' ? entry.code : undefined,
    message: typeof entry.message === 'string' ? entry.message : undefined,
    field: typeof entry.field === 'string' ? entry.field : undefined,
    appointmentUuid: typeof entry.appointment_uuid === 'string' ? entry.appointment_uuid : undefined
});

export const parseBookingErrorResponse = (data: unknown): BookingError => {
    if (isRecord(data)) {
        const errors = data.errors;

        if (Array.isArray(errors) && errors.length > 0 && isRecord(errors[0])) {
            const details = errors.filter(isRecord).map(toBookingErrorDetail);
            const first = details.find(detail => detail.code);

            if (first?.code) {
                return new BookingError(BookingErrorType.Coded, first.message, first.code, details);
            }
        }

        if ('appointments.booked_at' in data) {
            return new BookingError(BookingErrorType.AlreadyBooked);
        }
    }

    return new BookingError(BookingErrorType.Unknown);
};
